import { useState } from 'react'

import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useReconciliationJournalLinesDataTable,
} from 'generated/resources/datatable.graphql'
import {
  FinanceJournalLineFilter,
  FinanceValueObjectsJournalEntryTypeEnum,
  FinanceValueObjectsLedgerAccountBalanceTypeEnum,
} from 'generated/resources/graphql'
import { sum } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Amount } from '../../JournalsEntry/Datatable/_Amount'
import { EmptyBlock } from '../../ReconciliationSidepanel/EmptyBlock'
import { ReconciliationItem } from '../types'

interface Props {
  onSelect: (value: ReconciliationItem) => void
  onRemove: (value: ReconciliationItem) => void
  isSelected: (id: number) => boolean
  disabled: boolean
  currency: string
  balanceType: FinanceValueObjectsLedgerAccountBalanceTypeEnum[]
  excludingJournalEntryTypes?: FinanceValueObjectsJournalEntryTypeEnum[]
  allowedJournalEntryTypes?: FinanceValueObjectsJournalEntryTypeEnum[]
  onlyJournalLinesFromSuspenseLedgerAccount?: boolean
  onlyReconcilableJournalLines?: boolean
}

interface JournalLineDatatableFields {
  id: number
  description?: string
  creditAmountCents: number
  debitAmountCents: number
  reconcilableReconciliations: {
    __typename?: 'FinanceReconciliation'
    id: number
    reconcilableReconciledAmountCents?: number | null
  }[]
  reconciliations: {
    __typename?: 'FinanceReconciliation'
    id: number
    journalLineReconciledAmountCents?: number | null
  }[]
  journalEntry: {
    __typename?: 'FinanceJournalEntry'
    id: number
    description?: string | null
    number: number
    publishedAt: string
    referenceDate: string
  }
}

const getPendingAmount = (journalLine: JournalLineDatatableFields) => {
  const totalReconciledAmountCents =
    (journalLine.reconciliations.length > 0
      ? sum(journalLine.reconciliations.map((r) => r.journalLineReconciledAmountCents))
      : sum(
          journalLine.reconcilableReconciliations.map((r) => r.reconcilableReconciledAmountCents)
        )) || 0

  const journalLileTotalAmountCents =
    Math.abs(journalLine.creditAmountCents) + Math.abs(journalLine.debitAmountCents)
  return journalLileTotalAmountCents - totalReconciledAmountCents
}

export const ReconciliationJournalLinesDataTable = ({
  onSelect,
  onRemove,
  isSelected,
  disabled,
  currency,
  balanceType,
  allowedJournalEntryTypes,
  excludingJournalEntryTypes,
  onlyJournalLinesFromSuspenseLedgerAccount,
  onlyReconcilableJournalLines,
}: Props) => {
  const [filter, setFilter] = useState('')
  const onRowSelect = (_id: number) => {}

  const handleCheckboxChange =
    (journalLine: JournalLineDatatableFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const pendingAmountCents = getPendingAmount(journalLine)
      const reconciliation: ReconciliationItem = {
        id: journalLine.id,
        description: journalLine.journalEntry.description || '-',
        debitCentsToReconcile: journalLine.debitAmountCents > 0 ? pendingAmountCents : 0,
        creditCentsToReconcile: journalLine.creditAmountCents > 0 ? pendingAmountCents : 0,
        currency: currency || '',
        date: journalLine.journalEntry.publishedAt,
      }

      if (event.target.checked) {
        onSelect(reconciliation)
      } else {
        onRemove(reconciliation)
      }
    }

  const source = useReconciliationJournalLinesDataTable(
    [
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 0,
        render: (journalLine) => {
          return (
            <Box flexDirection="row">
              <Checkbox
                disabled={disabled}
                checked={isSelected(journalLine.id)}
                onChange={handleCheckboxChange(journalLine)}
              />
            </Box>
          )
        },
      },
      {
        identifier: 'publishedAt',
        title: i18n.t('finance.ledger_account_detail.datatable.fields.recorded_at'),
        kind: ProjectionKind.Custom,
        width: 130,
        render: ({ id, journalEntry_publishedAt }) => (
          <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
            <Text.Normal size="200">
              {dateBackendDateTimeToUI(String(journalEntry_publishedAt))}
            </Text.Normal>
          </Box>
        ),
        template: '{{ publishedAt }}',
      },
      {
        identifier: 'journalEntry_description',
        title: i18n.t(
          'finance.ledger_account_detail.datatable.fields.journal_entry_description_v2'
        ),
        kind: ProjectionKind.Custom,
        render: ({ id, journalEntry_description }) => (
          <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
            <Text.Normal size="200">{journalEntry_description}</Text.Normal>
          </Box>
        ),
        template: '{{ journalEntry_description }}',
      },
      ...(balanceType.includes('debit')
        ? [
            {
              identifier: 'debit',
              width: 120,
              title: i18n.t('finance.ledger_account_detail.datatable.fields.debit'),
              kind: ProjectionKind.Custom,
              render: ({ id, debitAmountCents }: { id: number; debitAmountCents: number }) => (
                <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
                  <Amount amountCents={debitAmountCents} currency={currency} />
                </Box>
              ),
              template: '{{ - }}',
            },
          ]
        : []),
      ...(balanceType.includes('credit')
        ? [
            {
              identifier: 'credit',
              width: 120,
              title: i18n.t('finance.ledger_account_detail.datatable.fields.credit'),
              kind: ProjectionKind.Custom,
              render: ({ id, creditAmountCents }: { id: number; creditAmountCents: number }) => (
                <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(id)}>
                  <Amount amountCents={creditAmountCents} currency={currency} />
                </Box>
              ),
              template: '{{ - }}',
            },
          ]
        : []),
      {
        identifier: 'pendingAmount',
        title: i18n.t('finance.invoices.list.fields.pendingAmount'),
        width: 130,
        kind: ProjectionKind.Custom,
        render: (journalLine) => {
          const pendingAmountCents = getPendingAmount(journalLine)
          return (
            <Box style={{ cursor: 'pointer' }} onClick={() => onRowSelect(journalLine.id)}>
              <Amount amountCents={pendingAmountCents} currency={currency} />
            </Box>
          )
        },
        template: '{{ totalAmountCents }}',
      },
    ],
    {
      variables: {
        description: filter,
        balanceTypes: balanceType,
        excludingJournalEntryTypes: excludingJournalEntryTypes,
        allowedJournalEntryTypes: allowedJournalEntryTypes,
        bySuspenseAccount: onlyJournalLinesFromSuspenseLedgerAccount,
        reconcilable: onlyReconcilableJournalLines,
        currency,
      },
    }
  )

  const filters: DataTableFilter<FinanceJournalLineFilter>[] = [
    {
      identifier: 'publishedAt',
      title: i18n.t('finance.ledger_account_detail.datatable.fields.recorded_at'),
      path: 'publishedAt',
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
        },
      ],
    },
  ]

  if (onlyReconcilableJournalLines === undefined) {
    filters.push({
      identifier: 'reconciledStatusFilter',
      title: i18n.t('finance.reconciliations.datatable.filters.reconciliation_type.title'),
      path: 'reconciledStatus',
      default: true,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          value: ['pending'],
          hideSearch: true,
          operation: 'in',
          options: [
            {
              label: i18n.t(
                'finance.reconciliations.datatable.filters.reconciliation_type.options.pending'
              ),
              value: 'pending',
            },
            {
              label: i18n.t(
                'finance.reconciliations.datatable.filters.reconciliation_type.options.reconciled'
              ),
              value: 'reconciled',
            },
          ],
        },
      ],
    })
  }

  return (
    <Box width="full">
      <DataTable
        disableEditColumns
        persistFilters={false}
        pagination={{
          itemsPerPage: 5,
          kind: 'offset',
        }}
        source={source}
        sort={{ path: 'id', direction: 'desc' }}
        filters={filters}
        disableExport
        search={{
          value: filter,
          onChange: (ev) => setFilter(ev.target.value),
          placeholder: i18n.t('finance.reconciliations.datatable.filters.description.title'),
        }}
        emptyState={<EmptyBlock />}
      />
    </Box>
  )
}
